import React, { createContext, useState, useContext, useCallback, useEffect, useRef, useMemo } from 'react';
import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from './AuthContext';
import NotificationToast from '../components/Shared/NotificationToast';
import { playVoice } from '../utils/voicePlayer';

const NotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  showToast: (message, options) => {},
  addNotification: (notification) => {},
  closeToast: () => {},
  fetchNotifications: () => {},
  markAsRead: (id) => {},
  markAllAsRead: () => {},
});

export const useNotifications = () => useContext(NotificationContext);
export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Global capsule toast state
  const [currentToast, setCurrentToast] = useState({
    show: false,
    message: '',
    type: 'info',
    theme: null,
    pathname: ''
  });
  const toastTimeoutRef = useRef(null);

  const { isConnected, subscribe } = useWebSocket();
  const { isAuthenticated } = useAuth();
  const subscriptionRef = useRef(null);

  // --- Show Toast function ---
  const showToast = useCallback((message, options = {}) => {
    if (!message) return;

    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }

    const toastData = typeof message === 'string'
      ? { message, ...options }
      : { message: message?.message || message?.title || '', ...message, ...options };

    const duration = toastData.duration || 3000;
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';

    setCurrentToast({
      show: true,
      message: toastData.message,
      type: toastData.type || (toastData.error ? 'error' : 'info'),
      theme: toastData.theme || null,
      pathname
    });

    toastTimeoutRef.current = setTimeout(() => {
      setCurrentToast(prev => ({ ...prev, show: false }));
      toastTimeoutRef.current = null;
    }, duration);
  }, []);

  const closeToast = useCallback(() => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
    setCurrentToast(prev => ({ ...prev, show: false }));
  }, []);

  // --- Add Notification (Feed + Toast) ---
  const addNotification = useCallback((notification) => {
    if (!notification) return;

    const isError = Boolean(
      notification.error ||
      notification.color === 'accent-red' ||
      notification.type === 'error' ||
      (notification.title && notification.title.toLowerCase().includes('error'))
    );
    const msg = notification.message || notification.title || 'Notification';

    const lowerMsg = typeof msg === 'string' ? msg.toLowerCase() : '';
    if (
      notification.type === 'plan' ||
      notification.type === 'upgrade' ||
      lowerMsg.includes('тариф') ||
      lowerMsg.includes('купить тариф') ||
      lowerMsg.includes('продлить тариф') ||
      lowerMsg.includes('insufficient plan') ||
      lowerMsg.includes('required plan') ||
      lowerMsg.includes('требуется тариф') ||
      lowerMsg.includes('обновить тариф')
    ) {
      playVoice('payment-request');
    }

    showToast(msg, {
      type: isError ? 'error' : (notification.type || 'info'),
      theme: notification.theme || null
    });

    const newNotification = {
      id: notification.id || `notif-${Date.now()}-${Math.random()}`,
      title: notification.title || 'Notification',
      message: notification.message || '',
      icon: notification.icon || (isError ? 'fa-exclamation-triangle' : 'fa-info-circle'),
      color: notification.color || (isError ? 'accent-red' : 'accent-blue'),
      time: notification.time || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      read: notification.read || false,
    };

    setNotifications(prev => [newNotification, ...prev].slice(0, 20));
    if (!newNotification.read) {
      setUnreadCount(prev => prev + 1);
    }
  }, [showToast]);

  // WebSocket Subscription
  useEffect(() => {
    if (isConnected && isAuthenticated) {
      if (subscriptionRef.current) subscriptionRef.current();
      subscriptionRef.current = subscribe('/user/queue/notifications', (message) => {
        if (message) {
          const errorMsg = message?.message || message?.error || '';
          const planErrorMatch = errorMsg.match(/Insufficient plan\. Required: (.*?)(?: plan)?$/i);
          if (planErrorMatch) {
            playVoice('payment-request');
          } else {
            addNotification({
              title: message.title || 'Server Update',
              message: message.message || message.error || 'Received update.',
              icon: message.icon || (message.error ? 'fa-exclamation-triangle' : 'fa-info-circle'),
              color: message.color || (message.error ? 'accent-red' : 'accent-blue'),
              type: message.error ? 'error' : 'info'
            });
          }
        }
      });
      return () => {
        if (subscriptionRef.current) subscriptionRef.current();
        subscriptionRef.current = null;
      };
    } else {
      if (subscriptionRef.current) subscriptionRef.current();
      subscriptionRef.current = null;
      closeToast();
    }
  }, [isConnected, isAuthenticated, subscribe, addNotification, closeToast]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const contextValue = useMemo(() => ({
    showToast,
    addNotification,
    closeToast,
    notifications,
    unreadCount,
    fetchNotifications: () => {},
    markAsRead: () => {},
    markAllAsRead: () => {},
  }), [showToast, addNotification, closeToast, notifications, unreadCount]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {/* Global Themed Capsule Toast */}
      <NotificationToast toast={currentToast} onClose={closeToast} />
    </NotificationContext.Provider>
  );
};